import { createIntl, createIntlCache } from 'react-intl';
import { makeRemoteGetAllClinicalDocs } from '~/main/factories/usecases/clinicalDocs/GetAllClinicalDocsFactory';
import { makeRemoteGetAppointmentById } from '~/main/factories/usecases/appointment/GetAppointmentbyIdFactory';
import { makeRemoteGetAllSupportDocs } from '~/main/factories/usecases/supportDoc/GetAllSupportDocsFactory';
import {
  iSession,
  iUser,
  AppointmentDetail,
  iClinicalDocs,
  iSupportDocs,
} from '../interface';
import translations from '~/infra/i18n/locales';
import { getLocale } from '~/utils/getLocale';
import { makeRemoteGetAppointmentType } from '~/main/factories/usecases/appointmentType/GetAppointmentType';

const cache = createIntlCache();

const intl = createIntl(
  {
    locale: String(getLocale()),
    messages: translations[getLocale()],
  },
  cache,
);

export const MapAppointmentDetail = async (
  appointmentID: number,
): Promise<AppointmentDetail> => {
  const locale = getLocale();

  const detail = await makeRemoteGetAppointmentById().getAppointmentById({
    id: appointmentID,
  });

  const cancelDate = new Date(detail.appointment.canceledAt)
    .toLocaleDateString(locale)
    .split('/');

  const cancelHour = new Date(detail.appointment.canceledAt).toLocaleTimeString(
    locale,
  );

  const date = new Date(detail.appointment.scheduled)
    .toLocaleDateString(locale)
    .split('/');

  const appointmentTypes = await makeRemoteGetAppointmentType().list({
    client: 'SAUDEMEET',
  });

  const month = [
    'JAN',
    'FEV',
    'MAR',
    'ABR',
    'MAI',
    'JUN',
    'JUL',
    'AGO',
    'SET',
    'OUT',
    'NOV',
    'DEZ',
  ];

  function makeTime() {
    const item = detail.appointment.scheduled;
    const time = new Date(item);
    const timeStart = new Date(time.getTime()).toLocaleTimeString(locale, {
      hour12: false,
    });

    const { duration } = detail.appointment;
    const timeEnd = new Date(
      time.getTime() + duration * 60000,
    ).toLocaleTimeString(locale, { hour12: false });

    const startFormat = `${timeStart.split(':')[0]}:${timeStart.split(':')[1]}`;
    const endFormat = `${timeEnd.split(':')[0]}:${timeEnd.split(':')[1]}`;
    return `${startFormat} - ${endFormat}`;
  }

  function countMonths() {
    const item = detail.consultant.birthDate?.split('T')[0];
    const today = Date.now();
    const birthDate = new Date(item);

    const diffTime = Math.abs(today - Number(birthDate));
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const age = Math.floor(diffDays / 365);
    let months = ((diffDays / 365) % 1) * 12;
    months = Math.trunc(months);

    return `${age} ${intl.formatMessage({
      id: 'anos e',
    })}  ${months} ${intl.formatMessage({ id: 'meses' })}`;
  }

  const appointmentType = appointmentTypes.find(
    item => item.id === detail.appointment.type,
  );

  const session: iSession = {
    status: detail.appointment.status,
    title: `${
      appointmentType?.msgKey
        ? intl.formatMessage({ id: appointmentType?.msgKey })
        : ''
    } - ${detail.specialty.name}`,
    day: date[0],
    mounth: month[Number(date[1]) - 1],
    timeInterval: makeTime(),
    cancelDate: `${intl.formatMessage({ id: 'em' })} ${cancelDate[0]}/${
      cancelDate[1]
    }/${cancelDate[2]} ${intl.formatMessage({ id: 'às' })} ${cancelHour}`,
  };

  const user: iUser = {
    id: detail.consultant?.id || 0,
    user: detail.consultant.user,
    name: `${detail.professional?.firstName} ${detail.professional?.lastName}`,
    recordAdvice: `${detail.professional?.docProf} - ${detail.professional?.docProfUF}`,
    specialty: detail.specialty.name,
  };

  return { user, session };
};

export const MapCLinicalDocuments = async (
  appointmentID: number,
): Promise<iClinicalDocs[]> => {
  const docs = (await makeRemoteGetAllClinicalDocs()
    .getAll({
      filter: {
        appointment: appointmentID,
        enabled: true,
      },
    })
    .catch(err => console.log('err: ', err))) ?? { results: [] };

  const arr: iClinicalDocs[] = [];

  docs.results.forEach(item => {
    arr.push({
      document: {
        id: item.document.id,
        sent: item.document.sent,
      },
      type: item.type,
      signed: item.document.status === 'SIGNED',
      index: item.index.index,
      content: item.document.content, // TODO: Atualizar content
    });
  });

  return arr;
};

export const MapSupportingDocuments = async (
  appointmentID: number,
): Promise<iSupportDocs[]> => {
  const docs = (await makeRemoteGetAllSupportDocs()
    .getAll({
      appointment: appointmentID,
    })
    .catch(err => console.log('err: ', err))) ?? { results: [] };

  const arr: iSupportDocs[] = [];

  docs.results.forEach(item => {
    arr.push({
      document: {
        id: item.document.id,
        size: item.document.size,
        token: item.document.token,
        sent: item.document.sent,
      },
      sender: item.sender?.id
        ? {
            id: item.sender?.id,
            name: `${item.sender?.firstName} ${item.sender?.lastName}`,
          }
        : undefined,
      type: {
        id: item.type.id,
        name: item.type.name,
      },
    });
  });

  return arr;
};
